/**
 * Approved names and their other spellings (migration 138: bv_name,
 * bv_name_alias), for matching done in the app rather than the database.
 *
 * The same rule as the database's bv_canonical_name: fold case and drop
 * everything but letters and digits, then look the key up as a System Name,
 * then as an alias. A spelling that is neither comes back trimmed and
 * unchanged, so an unknown name still matches exactly as it did before.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

export type NameKind = 'retailer' | 'distributor' | 'metering';

/** Name key → System Name. */
export type NameAliases = ReadonlyMap<string, string>;

/** "Ergon Energy (Distribution)" → "ergonenergydistribution". Mirrors bv_name_key. */
export const nameKey = (v: string | null | undefined): string =>
  (v ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** The System Name a spelling stands for, or the spelling itself (trimmed). */
export const canonicalName = (aliases: NameAliases | undefined, v: string | null | undefined): string => {
  const raw = (v ?? '').trim();
  return aliases?.get(nameKey(raw)) ?? raw;
};

/**
 * Builds the lookup from rows read off bv_name and bv_name_alias. A System
 * Name wins over an alias with the same key, as in the database.
 */
export const buildNameAliases = (
  names: ReadonlyArray<{ system_name: string }>,
  aliases: ReadonlyArray<{ alias: string; system_name: string }>,
): NameAliases => {
  const out = new Map<string, string>();
  for (const a of aliases) out.set(nameKey(a.alias), a.system_name);
  for (const n of names) out.set(nameKey(n.system_name), n.system_name);
  return out;
};

/**
 * One kind's names, read with whichever client the caller holds. A failed read
 * returns an empty lookup: matching then falls back to the plain spelling,
 * which is the behaviour before the alias table existed.
 */
export const loadNameAliases = async (db: SupabaseClient, kind: NameKind): Promise<NameAliases> => {
  try {
    const [names, aliases] = await Promise.all([
      db.from('bv_name').select('system_name').eq('kind', kind),
      db.from('bv_name_alias').select('alias, system_name').eq('kind', kind),
    ]);
    if (names.error || aliases.error) throw names.error ?? aliases.error;
    return buildNameAliases(
      (names.data ?? []) as { system_name: string }[],
      (aliases.data ?? []) as { alias: string; system_name: string }[],
    );
  } catch (err) {
    console.error(`Name aliases (${kind}) load failed:`, err);
    return new Map();
  }
};
